import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDButton from "components/MDButton";
import { useAuth } from "pages/context/AuthContext";

/**
 * Mostra um menu ligado ao utilizador:
 * - Se autenticado: opção "Terminar sessão" (logout).
 * - Se não autenticado: opção "Iniciar sessão" (navega para /auth/login).
 *
 * Aceita opcionalmente `items` para continuar a listar PDFs (se quiseres manter essa funcionalidade).
 */
export default function UserMenu({
  basePath = "/docs",
  items, // [{ label, file }]
}) {
  const navigate = useNavigate();
  const { isAuthenticated, logout, userData } = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const defaultItems = useMemo(() => [], []);
  const list = items?.length ? items : defaultItems;

  const open = Boolean(anchorEl);
  const onOpen = (e) => setAnchorEl(e.currentTarget);
  const onClose = () => setAnchorEl(null);

  const handleOpenPdf = (file) => {
    const url = `${basePath}/${encodeURIComponent(file)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  const handleLogin = () => {
    onClose();
    navigate("/auth/login");
  };

  const handleAskLogout = () => {
    onClose();
    setOpenDialog(true);
  };

  const handleDoLogout = async () => {
    setOpenDialog(false);
    await logout();
    navigate("/auth/login");
  };

  return (
    <>
      <Tooltip title={isAuthenticated ? userData?.designacao || "Utilizador" : "Iniciar sessão"}>
        <IconButton
          size="small"
          onClick={onOpen}
          sx={{
            ml: 1,
            color: (t) => t.palette.warning.main,
            width: 36,
            height: 36,
            "&:hover": { bgcolor: "rgba(255,152,0,0.10)" },
          }}
        >
          <Icon>account_circle</Icon>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        keepMounted
        MenuListProps={{ dense: true }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1,
            borderRadius: 2,
            minWidth: 260,
            "& .MuiMenuItem-root": { py: 0.5 },
            "& .MuiListItemIcon-root": { minWidth: 28 },
            "& .MuiListItemText-primary": { fontSize: 14.5 },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {list.length > 0 && (
          <>
            {list.map((it) => (
              <MenuItem key={it.file || it.label} onClick={() => handleOpenPdf(it.file)}>
                <ListItemIcon>
                  <Icon fontSize="small">picture_as_pdf</Icon>
                </ListItemIcon>
                <ListItemText primary={it.label} />
              </MenuItem>
            ))}
            <Divider sx={{ my: 0.5 }} />
          </>
        )}

        {isAuthenticated ? (
          <MenuItem onClick={handleAskLogout}>
            <ListItemIcon>
              <Icon fontSize="small">logout</Icon>
            </ListItemIcon>
            <ListItemText primary="Terminar sessão" />
          </MenuItem>
        ) : (
          <MenuItem onClick={handleLogin}>
            <ListItemIcon>
              <Icon fontSize="small">login</Icon>
            </ListItemIcon>
            <ListItemText primary="Iniciar sessão" />
          </MenuItem>
        )}
      </Menu>

      {/* Confirmação de logout */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Terminar sessão</DialogTitle>
        <DialogContent dividers>Tem a certeza que pretende terminar a sessão?</DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenDialog(false)} variant="outlined" color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleDoLogout} variant="contained" color="info">
            Sair
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
